import React from 'react';
import { PageStatIcon } from '../../components/PageStickyChrome/PageStatIcons';
import { isActiveDocument, isReadyDocument } from '../../helper/DeviceDocumentsHelper';

const PROCESS_LABEL = {
  pending: 'Pending',
  processing: 'Processing',
  ready: 'Ready',
  failed: 'Failed',
};

const ProcessBadge = ({ status }) => {
  const key = String(status || 'pending').toLowerCase();
  return (
    <span className={`docs-badge docs-badge--${key}`}>
      {key === 'processing' ? <span className="docs-spinner" aria-hidden="true" /> : null}
      {PROCESS_LABEL[key] || status || 'Pending'}
    </span>
  );
};

const UserManualsPanel = ({
  documents,
  models,
  selectedModel,
  onModelChange,
  highlightedId,
  loading,
  onUploadClick,
  onProcess,
  onSetDefault,
  onRemoveDefault,
  onAssign,
  processingIds,
}) => {
  const filtered = selectedModel
    ? documents.filter((doc) => doc.deviceModel === selectedModel)
    : documents;
  const readyCount = filtered.filter(isReadyDocument).length;
  const defaultCount = filtered.filter(isActiveDocument).length;

  return (
    <>
      <div className="docs-filters-bar">
        <select
          className="docs-filter-select"
          value={selectedModel}
          onChange={(event) => onModelChange(event.target.value)}
          aria-label="Filter by product"
        >
          <option value="">All products</option>
          {models.map((model) => (
            <option key={model} value={model}>{model}</option>
          ))}
        </select>
        <button type="button" className="docs-btn docs-btn--primary" onClick={onUploadClick}>
          Upload PDF
        </button>
      </div>

      <div className="docs-stats-grid">
        <div className="docs-stat-card">
          <PageStatIcon name="document" />
          <span className="docs-stat-label">VERSIONS</span>
          <span className="docs-stat-value">{filtered.length}</span>
        </div>
        <div className="docs-stat-card">
          <PageStatIcon name="success" />
          <span className="docs-stat-label">READY</span>
          <span className="docs-stat-value">{readyCount}</span>
        </div>
        <div className="docs-stat-card">
          <PageStatIcon name="hospital" />
          <span className="docs-stat-label">DEFAULTS</span>
          <span className="docs-stat-value">{defaultCount}</span>
        </div>
      </div>

      <div className="docs-toolbar">
        <h2 className="docs-section-title">Manual versions</h2>
      </div>

      {loading && documents.length === 0 ? (
        <div className="docs-empty">Loading manuals…</div>
      ) : filtered.length === 0 ? (
        <div className="docs-empty">No manuals yet. Upload a PDF to create the first version.</div>
      ) : (
        <div className="docs-table-wrap">
          <table className="docs-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Version</th>
                <th>Title</th>
                <th>Pages</th>
                <th>Process</th>
                <th>Default</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((doc) => {
                const process = String(doc.processingStatus || 'pending').toLowerCase();
                const ready = isReadyDocument(doc);
                const active = isActiveDocument(doc);
                const inFlight = processingIds.has(doc.id) || process === 'processing';

                return (
                  <tr
                    key={doc.id}
                    className={highlightedId === doc.id ? 'docs-row--highlight' : undefined}
                  >
                    <td>{doc.deviceModel || '—'}</td>
                    <td>
                      <span className="docs-cell-title">{doc.version || '—'}</span>
                      <span className="docs-id">#{doc.id}</span>
                    </td>
                    <td>{doc.title || '—'}</td>
                    <td>{doc.pageCount || 0}</td>
                    <td>
                      <ProcessBadge status={inFlight && process !== 'failed' ? 'processing' : process} />
                      {process === 'failed' && doc.processingError ? (
                        <span className="docs-error-text">{doc.processingError}</span>
                      ) : null}
                    </td>
                    <td>
                      <span className={`docs-badge ${active ? 'docs-badge--active' : 'docs-badge--inactive'}`}>
                        {active ? 'Default' : 'Off'}
                      </span>
                    </td>
                    <td>
                      <div className="docs-actions">
                        {(process === 'pending' || process === 'failed') && !inFlight ? (
                          <button
                            type="button"
                            className="docs-btn docs-btn--ghost docs-btn--small"
                            onClick={() => onProcess(doc)}
                          >
                            {process === 'failed' ? 'Retry process' : 'Process'}
                          </button>
                        ) : null}
                        {ready ? (
                          <>
                            {active ? (
                              <button
                                type="button"
                                className="docs-btn docs-btn--ghost docs-btn--small"
                                onClick={() => onRemoveDefault(doc)}
                              >
                                Remove default
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="docs-btn docs-btn--primary docs-btn--small"
                                onClick={() => onSetDefault(doc)}
                              >
                                Set as default
                              </button>
                            )}
                            <button
                              type="button"
                              className="docs-btn docs-btn--ghost docs-btn--small"
                              onClick={() => onAssign(doc)}
                            >
                              Assign version
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default UserManualsPanel;
